import React, { useState, useEffect, useContext } from "react";
import { Auth0Client } from "@auth0/auth0-spa-js";
import createAuth0ClientMock from "../tests/helpers/auth0Helpers";
//import { addOrgMetadata } from "../awsRum";

// Drops the ?code=&state= Auth0 appends to the callback URL, leaving the plain
// path behind.
const clearRedirectParams = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const DEFAULT_REDIRECT_CALLBACK = clearRedirectParams;

// The library's own createAuth0Client() is `new Auth0Client()` followed by
// checkSession(), and checkSession rethrows every error outside its short
// recoverable list ("login_required" and friends). So a silent-auth iframe that
// times out because third-party cookies are blocked, a network blip, or a
// refresh token the tenant no longer honours all escape - and because the
// rejection happens inside the factory, the caller is left with no client at
// all, not even one to log in with.
//
// Doing the two steps separately keeps a usable client in hand when the session
// check fails. Failing to silently restore a session is not an error worth
// surfacing: it means the user is not logged in, which is where every first
// visit starts.
//
// This is what made the bug look like a browser-state problem. A fresh
// incognito window has no "auth0.<client>.is.authenticated" cookie, so
// checkSession returns early without ever reaching the network and nothing
// throws; the same browser after one successful login has the cookie, tries the
// silent check on every visit, and fails the same way every time.
const buildClient = async (options) => {
  const client = new Auth0Client(options);
  try {
    await client.checkSession();
  } catch (e) {
    console.warn("Auth0 session check failed; continuing unauthenticated", e);
  }
  return client;
};

const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      setLoading(true);
      try {
        const auth0FromHook =
          process.env.REACT_APP_TEST_STATUS === "test"
            ? await createAuth0ClientMock(initOptions)
            : await buildClient(initOptions);
        setAuth0(auth0FromHook);

        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          try {
            const { appState } = await auth0FromHook.handleRedirectCallback();
            onRedirectCallback(appState);
          } catch (e) {
            // A callback URL that can no longer be exchanged: the code has
            // already been spent, or the transaction that issued it is gone
            // (the browser went Back onto the callback, the login finished in
            // another tab, the transaction cookie was dropped). Strip the
            // parameters so the URL the user is now sitting on - and any
            // reload or Back that returns to it - is a clean start instead of
            // the same failure again, then carry on unauthenticated so the
            // login redirect below can take over.
            console.warn("Auth0 redirect callback failed", e);
            clearRedirectParams();
          }
        }

        const isAuthenticated = await auth0FromHook.isAuthenticated();
        setIsAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          const user = await auth0FromHook.getUser();
          setUser(user);
        }
      } catch (e) {
        // Nothing here is worth a white screen. Whatever failed, the honest
        // state is "not logged in", which PrivateRoute turns into a trip to the
        // login screen.
        console.error("Auth0 initialisation failed", e);
        setIsAuthenticated(false);
      } finally {
        // Always, on every path. App.js renders nothing at all until this flips
        // to false, so an init that neither resolves nor rejects its way here
        // is a permanently blank page.
        setLoading(false);
      }
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setIsAuthenticated(true);
    setUser(user);
    setLoading(false);
  };
  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (...p) => auth0Client.logout(...p),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
